import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tour } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { useMediaQuery } from '@/hooks/useBreakpoint'
import { useOrganizerShell } from '@/components/Organizer/OrganizerShell/OrganizerShellContext'
import { NARROW_QUERY } from '@/components/Organizer/OrganizerShell/useOrgSideState'
import { relativeEventPath } from '@/components/Organizer/OrganizerShell/orgMenu.active'
import { getTour } from './tours.registry'
import {
    closeOutcome,
    entryNamespaces,
    menuHoldOf,
    navigateOf,
    resolveSteps,
    sectionHoldOf,
    stepPlacement,
    tourAllowsLocation,
    tourScope,
} from './tours.utils'
import { bandOf, blockTarget, findTarget, isTargetVisible, prepareTarget, waitForTarget } from './tourTargets'
import TourInvite from './TourInvite'
import useTourInvite from './useTourInvite'
import useTourProgress from './useTourProgress'
import useTourStore from './tourStore'

import './tours.css'

const MASK = { color: 'rgba(39, 39, 39, 0.45)' }
const GAP = { offset: 6, radius: 10 }
// instant, not smooth: antd aligns the bubble once, right after scrolling the target into view, and a
// smooth scroll left it hanging off-screen for seconds on the page tours (28 Oct 2026)
const SCROLL = { block: 'center', behavior: 'auto' }
const TOUR_Z = 1100 // above the ≤ 1100 drawer (150), the mobile nav (200) and the cookies card (75)
// under this width a 360-px bubble no longer fits beside the 280-px drawer: steps use `placementNarrow`
const PHONE_QUERY = '(max-width: 699px)'

// a short macrotask, not requestAnimationFrame: the menu hold must be committed and laid out before
// antd measures the target, and rAF is throttled or dropped in a background tab. When the hold
// changes (rail → panel or back) the side menu animates its width (index.css, ~180 ms): wait it out,
// or antd measures a 64-px rail and hangs the bubble over the menu.
const AFTER_LAYOUT_MS = 40
const AFTER_MENU_MOTION_MS = 260
const FIRST_TARGET_WAIT_MS = 3000
const ROUTE_TARGET_WAIT_MS = 6000 // a hub tab loads its data before its anchor paints (gallery: albums first)
const afterLayout = (fn, ms = AFTER_LAYOUT_MS) => setTimeout(fn, ms)
// antd aligns the bubble once, as soon as the step shows, while the page is still settling (a section
// just opened, the window just scrolled, a tab still loading): a bubble then stays on a stale rect or
// is never aligned at all (rc-trigger parks it at `inset: -1000vh -1000vw` and sometimes needs more
// than one nudge). rc-trigger and rc-tour both re-measure on a window resize, so after each step the
// host nudges them twice, then keeps nudging every 250 ms while the bubble is still parked (28 Oct 2026).
const REALIGN_MS = [120, 320, 800] // 800: a panel a step opens itself (home advanced search) still mounts its fields
const REALIGN_POLL_MS = 100 // a parked bubble took 4-5 nudges to settle on a phone (scores head)
const REALIGN_GIVE_UP_MS = 4000
const nudge = () => window.dispatchEvent(new Event('resize'))
const bubbleParked = () => {
    const bubble = document.querySelector('.ant-tour:not(.ant-tour-mask)')
    return !!bubble && /-1000v/.test(bubble.style.top || bubble.style.inset || '')
}
const realign = () => {
    REALIGN_MS.forEach((ms) => setTimeout(nudge, ms))
    const started = Date.now()
    const poll = () => {
        if (Date.now() - started > REALIGN_GIVE_UP_MS || !bubbleParked()) return
        nudge()
        setTimeout(poll, REALIGN_POLL_MS)
    }
    setTimeout(poll, REALIGN_MS[0] + REALIGN_POLL_MS)
}
const holdChanges = (before, after) => !!before !== !!after

/**
 * Host of the guided tours (antd Tour), mounted twice: by OrganizerShell for the organizer tours
 * (`scope` 'organizer': dashboard + page tours, needs the shell) and by App.js at the root for the
 * site tours (`scope` 'site': home page, « My events »); each host only plays the tours of its scope.
 * Plays the tour the store names: resolves which steps apply now (width, gates, targets on screen),
 * asks the side menu to stay unfolded / open a group — or an accordion page to open a section card —
 * one frame before each step so the bubble measures a painted target, runs a step's `enter` / `leave`
 * hooks, shows the route a step asks for (the hub tabs, back to the starting one at the end), records
 * the outcome on the account when the tour ends (Finish, ×, Escape) and stops silently on a navigation
 * out of the tour's routes, or on a click in the dimmed area (William, 29 Oct 2026: « si on clique dans
 * la zone grisée alors fermer le guide », recorded like ×). Also owns the first contact (useTourInvite):
 * a tour never seen starts by itself at the first visit of its page, then the invitation card offers it
 * (29 Oct 2026). The dashboard tour never leaves the side menu (William, 28 Oct 2026): the hub content
 * has its own page tour, like every organizer page (lot 2).
 */
export default function OrganizerTour({ scope = 'organizer' }) {
    const shell = useOrganizerShell()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const narrow = useMediaQuery(NARROW_QUERY)
    const phone = useMediaQuery(PHONE_QUERY)
    const activeId = useTourStore((state) => state.activeId)
    const activeDef = activeId ? getTour(activeId) : null
    // the other host plays the tours of the other scope
    const def = activeDef && tourScope(activeDef) === scope ? activeDef : null
    // the `tours` namespace plus every namespace the playing tour's entry labels borrow (the labels
    // are the UI's own keys: a menu row, a button, a column head)
    const namespaces = useMemo(() => [TranslationNS.TOURS, ...entryNamespaces(def)], [def])
    const { t } = useTranslation(namespaces)
    const progress = useTourProgress()
    const { markDone } = progress

    const current = useTourStore((state) => state.current)
    const setCurrent = useTourStore((state) => state.setCurrent)
    const setMenuHold = useTourStore((state) => state.setMenuHold)
    const setSectionHold = useTourStore((state) => state.setSectionHold)
    const stop = useTourStore((state) => state.stop)

    const { invite, accept, later, never } = useTourInvite(shell, scope, progress)

    const [steps, setSteps] = useState(null)
    const [open, setOpen] = useState(false)

    const ctx = useMemo(
        () => ({
            narrow,
            groups: shell?.menu,
            isHub: !!shell?.isHub,
            hasTarget: isTargetVisible,
        }),
        [narrow, shell?.menu, shell?.isHub],
    )
    const ctxRef = useRef(ctx)
    ctxRef.current = ctx

    // a tour starts: resolve its steps, set the first step's menu hold, open one frame later
    useEffect(() => {
        if (!def) {
            setSteps(null)
            setOpen(false)
            return undefined
        }
        let cancelled = false
        let timer = 0
        const begin = (resolved) => {
            setSteps(resolved)
            const firstStep = resolved[Math.min(current, resolved.length - 1)]
            const firstHold = menuHoldOf(firstStep)
            setMenuHold(firstHold)
            setSectionHold(sectionHoldOf(firstStep))
            firstStep.enter?.()
            timer = afterLayout(
                () => {
                    if (cancelled) return
                    prepareTarget(firstStep)
                    setOpen(true)
                    realign()
                },
                holdChanges(null, firstHold) ? AFTER_MENU_MOTION_MS : AFTER_LAYOUT_MS,
            )
        }
        const resolved = resolveSteps(def, ctxRef.current)
        if (resolved.length) {
            begin(resolved)
        } else {
            // started while the page is still loading (a tap on « Cette page » right after landing):
            // give the first anchor a moment to paint, then give up quietly
            const first = def.steps.find((step) => step.target)?.target
            waitForTarget(first || '[data-tour="side-menu"]', FIRST_TARGET_WAIT_MS).then(() => {
                if (cancelled) return
                const retried = resolveSteps(def, ctxRef.current)
                if (retried.length) begin(retried)
                else stop()
            })
        }
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [def?.id])

    // navigating out of the tour's routes stops it without recording anything (a step's own
    // `navigate` stays inside them: the hub tabs of the event-page tour)
    const startPath = useRef(pathname)
    useEffect(() => {
        startPath.current = pathname
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId])
    useEffect(() => {
        if (!def || startPath.current === pathname) return
        if (!tourAllowsLocation(def, { pathname, rel: relativeEventPath(pathname, shell?.base || '') })) stop()
    }, [def, pathname, shell?.base, stop])

    // Crisp's bubble hides while a tour plays (tours.css)
    useEffect(() => {
        document.body.classList.toggle('org-tour-open', !!def)
        return () => document.body.classList.remove('org-tour-open')
    }, [def])

    const base = shell?.base || ''
    const pathRef = useRef(pathname)
    pathRef.current = pathname

    const goTo = useCallback(
        (next) => {
            const step = steps?.[next]
            if (!step) return
            const before = menuHoldOf(steps[current])
            const after = menuHoldOf(step)
            setMenuHold(after)
            setSectionHold(sectionHoldOf(step))
            steps[current]?.leave?.()
            step.enter?.()
            const route = navigateOf(step)
            if (route !== null && relativeEventPath(pathRef.current, base) !== route) {
                // the step lives on another route of the tour (a hub tab): show it, let its anchor paint
                navigate(`${base}/${route}`.replace(/\/+$/, ''))
                waitForTarget(step.target, ROUTE_TARGET_WAIT_MS).then(() =>
                    afterLayout(() => {
                        prepareTarget(step)
                        setCurrent(next)
                        realign()
                    }),
                )
                return
            }
            afterLayout(
                () => {
                    prepareTarget(step)
                    setCurrent(next)
                    realign()
                },
                holdChanges(before, after) ? AFTER_MENU_MOTION_MS : AFTER_LAYOUT_MS,
            )
        },
        [steps, current, setMenuHold, setSectionHold, setCurrent, navigate, base],
    )

    const finish = useCallback(
        (outcome) => {
            if (def) markDone(def, outcome)
            steps?.[current]?.leave?.()
            setOpen(false)
            stop()
            // a tour that walked through other routes (hub tabs) leaves the page where it found it
            if (startPath.current && startPath.current !== pathRef.current) navigate(startPath.current)
        },
        [def, steps, current, markDone, stop, navigate],
    )

    const total = steps?.length || 0
    const onClose = useCallback((index) => finish(closeOutcome(index, total)), [finish, total])

    // Escape closes like × (rc-tour has no keyboard handling of its own)
    useEffect(() => {
        if (!open) return undefined
        const onKey = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return
            event.preventDefault()
            onClose(current)
        }
        window.addEventListener('keydown', onKey, true)
        return () => window.removeEventListener('keydown', onKey, true)
    }, [open, current, onClose])

    // a click on the dimmed area closes like × (William, 29 Oct 2026). rc-tour's mask is a fixed
    // `div.ant-tour-mask` whose SVG cover rects swallow the clicks around the target and do nothing with
    // them; the bubble and the target's hole are outside it, so their clicks are untouched
    useEffect(() => {
        if (!open) return undefined
        const onClick = (event) => {
            if (event.defaultPrevented || !event.target?.closest?.('.ant-tour-mask')) return
            event.preventDefault()
            onClose(current)
        }
        window.addEventListener('click', onClick, true)
        return () => window.removeEventListener('click', onClick, true)
    }, [open, current, onClose])

    const antdSteps = useMemo(
        () =>
            (steps || []).map((step, index) => {
                // a phone-sized screen drops the entry lines of a step that says so: a 295-px bubble wraps
                // every line, and five of them no longer fit under the target
                const entries = phone && step.entriesDesktopOnly ? [] : step.entries || []
                return {
                    title: t(step.title),
                    description: (
                        <>
                            <p className="sf-tour-intro">
                                {t(step.description, step.vars ? step.vars(ctx) : undefined)}
                            </p>
                            {entries.length ? (
                                <ul className="sf-tour-list">
                                    {entries.map((entry) => (
                                        <li key={entry.label}>
                                            <b>{t(entry.label, { ns: entry.ns || TranslationNS.TOURS })}</b>
                                            {' – '}
                                            {t(entry.text)}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </>
                    ),
                    target: step.target
                        ? () =>
                              (step.section || step.block
                                  ? blockTarget(step.target, bandOf(step), entries.length)
                                  : findTarget(step.target)) || document.querySelector(step.target)
                        : null,
                    placement: stepPlacement(step, phone),
                    prevButtonProps: { children: t('Back') },
                    nextButtonProps: { children: index === steps.length - 1 ? t('Finish') : t('Next') },
                }
            }),
        [steps, phone, ctx, t],
    )

    const indicators = useCallback(
        (index, count) => <span className="sf-tour-count">{`${index + 1} / ${count}`}</span>,
        [],
    )

    return (
        <>
            {invite ? <TourInvite def={invite} onAccept={accept} onLater={later} onNever={never} /> : null}
            {def && steps && open ? (
                <Tour
                    open
                    current={Math.min(current, steps.length - 1)}
                    steps={antdSteps}
                    onChange={goTo}
                    onClose={onClose}
                    mask={MASK}
                    gap={GAP}
                    zIndex={TOUR_Z}
                    rootClassName="sf-tour"
                    scrollIntoViewOptions={SCROLL}
                    indicatorsRender={indicators}
                />
            ) : null}
        </>
    )
}
